import { PIPEFITTER_CHART_COLORS } from '../constants/uiConstants';

export const projectStatusHelper = (projectStatusPipeData: any[], pipeCounts?: any) => {
  const initialWebsiteBlogData = projectStatusPipeData?.map((item: any) => item.count) || [];

  const maxPipeScan = Math.max(...initialWebsiteBlogData, 0);

  const cumulativeData = initialWebsiteBlogData.reduce((accumulator: number[], value: number) => {
    const cumulativeSum =
      accumulator.length > 0
        ? accumulator[accumulator.length - 1] + value
        : value;
    return [...accumulator, cumulativeSum];
  }, []);

  const availablePipes = pipeCounts?.pipeCount || 0;
  const usedPipes = pipeCounts?.usedPipeCount || 0;
  const validPipes = availablePipes + usedPipes;

  const series = [
    {
      name: 'Number of Scan/Day',
      type: 'column',
      data: initialWebsiteBlogData,
    },
    {
      name: 'CNS',
      type: 'line',
      data: cumulativeData,
    },
  ];

  const options = {
    grid: {
      borderColor: PIPEFITTER_CHART_COLORS.border,
    },
    chart: {
      height: 400,
      type: 'line' as const,
      foreColor: PIPEFITTER_CHART_COLORS.foreground,
      toolbar: {
        show: true,
      },
    },
    stroke: {
      width: [0, 4],
    },
    dataLabels: {
      enabled: false,
      enabledOnSeries: [1],
    },
    labels: projectStatusPipeData?.map((item: any) => item.date) || [],
    xaxis: {
      type: 'datetime' as const,
      labels: {
        style: {
          fontWeight: '400',
          fontSize: '12px',
        },
      },
    },
    yaxis: [
      {
        title: {
          text: 'Number of Pipe Scan',
        },
        tickAmount: 5,
        min: 0,
        max: maxPipeScan + 10,
      },
      {
        opposite: true,
        tickAmount: 5,
        min: 0,
        max: cumulativeData[cumulativeData.length - 1] || 0,
      },
    ],
    colors: [PIPEFITTER_CHART_COLORS.primary, PIPEFITTER_CHART_COLORS.secondaryOrange, PIPEFITTER_CHART_COLORS.grayDark, PIPEFITTER_CHART_COLORS.secondaryGreen, PIPEFITTER_CHART_COLORS.gray],
    legend: {
      show: true,
    },
    tooltip: {
      theme: 'dark' as const,
    },
  };

  return { series, options, validPipes };
};

const toNumbers = (arr: (string | number)[] | undefined): number[] =>
  Array.isArray(arr) ? arr.map((v) => Number(v)).filter((v) => !Number.isNaN(v)) : [];

const toStringBins = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.map((v) => String(v)) : [];

const isAllZeros = (arr: number[]) => arr.length > 0 && arr.every((num) => num === 0);

/** Joint-series fields used by the PDF compatibility report graph. */
export function pickPdfCompatibilityChartSeries(projectSummaryData: Record<string, unknown> | null | undefined) {
  const data = projectSummaryData ?? {};
  return {
    bins: toStringBins(data.rangeHiloJoints ?? data.positiveRangeHilo),
    omnifit: toNumbers(
      (data.numberOfJointsPerHiloForGraph ??
        data.numberOfJointsPerHilo ??
        data.numberOfPipesPerHilo) as (string | number)[] | undefined
    ),
    optifit: toNumbers(
      (data.numberOfJointsPerHiloOptifitForGraph ??
        data.numberOfJointsPerHiloOptifit ??
        data.numberOfPipesPerHiloOptifit) as (string | number)[] | undefined
    ),
    random: toNumbers(
      (data.numberOfJointsPerHiloInRandomForGraph ??
        data.numberOfJointsPerHiloInRandom ??
        data.numberOfPipesPerHiloInRandom) as (string | number)[] | undefined
    ),
  };
}

export function hasProjectSummaryChartData(projectSummaryData: Record<string, unknown> | null | undefined): boolean {
  const { omnifit, optifit, random } = pickPdfCompatibilityChartSeries(projectSummaryData);
  return !(isAllZeros(omnifit) && isAllZeros(optifit) && isAllZeros(random));
}

export const projectSummaryHelper = (projectSummaryData: any) => {
  const { bins, omnifit, optifit, random } = pickPdfCompatibilityChartSeries(projectSummaryData);

  return {
    series: [
      {
        name: 'Omnifit',
        data: omnifit,
      },
      {
        name: 'Optifit',
        data: optifit,
      },
      {
        name: 'Random',
        data: random,
      },
    ],
    options: {
      grid: {
        borderColor: PIPEFITTER_CHART_COLORS.border,
      },
      chart: {
        type: 'bar' as const,
        height: 350,
        foreColor: PIPEFITTER_CHART_COLORS.foreground,
        toolbar: {
          show: true,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        title: {
          text: 'HILO (mm)',
        },
        categories: bins,
      },
      yaxis: {
        title: {
          text: 'Number of Joints',
        },
        tickAmount: 5,
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        theme: 'dark' as const,
      },
      colors: [PIPEFITTER_CHART_COLORS.primary, PIPEFITTER_CHART_COLORS.gray, PIPEFITTER_CHART_COLORS.secondaryGreen],
      legend: {
        show: true,
      },
    },
  };
};
